import type { ReactNode } from "react";
import { useMemo } from "react";
import { Typography, useMediaQuery } from "@mui/material";
import type { SxProps, TypographyProps } from "@mui/material";
import statutMap, { unknownStatus } from "constants/SatutMap";
import type Statut from "constants/Statut";
import { useDebounce } from "use-debounce";
import ApplicationRoles from "constants/ApplicationRoles";
import { useUser } from "@sdeapps/react-core";

interface StatutChipProps extends TypographyProps {
  statut: Statut;
  isSmall?: boolean;
  sx?: SxProps;
}

function StatutChip({
  statut,
  variant = "body2",
  isSmall = false,
  sx,
  ...typographyProps
}: Readonly<StatutChipProps>): ReactNode {
  const user = useUser();
  const sizeIsSmall = useMediaQuery((theme) => theme.breakpoints.down("md"));
  // on debounce isSmallScreen pour une question de performance, trop de rerenders inutiles sinon
  const [debouncedIsSmallScreen] = useDebounce(isSmall || sizeIsSmall, 500);

  const chipAssociation = useMemo(() => statutMap.get(statut) ?? unknownStatus, [statut]);

  return (
    <Typography
      variant={variant}
      color={`${chipAssociation.color}.main`}
      {...typographyProps}
      sx={{
        backgroundColor: `${chipAssociation.color}.light`,
        paddingX: 1,
        paddingY: 0.5,
        fontWeight: 600,
        borderRadius: 1,
        display: "inline-block",
        ...sx,
      }}>
      {user.isRoles([ApplicationRoles.SSTMANAGER, ApplicationRoles.STOREMANAGER]) &&
      debouncedIsSmallScreen
        ? chipAssociation.smallLabel
        : chipAssociation.label}
    </Typography>
  );
}

export default StatutChip;
